<script>
  import { push, link } from 'svelte-spa-router';
  import { FileText, Plus, Pencil, Trash2 } from '../lib/icons/index.js';
  import Layout from '../components/Layout.svelte';
  import InvoiceTabs from '../components/InvoiceTabs.svelte';
  import { t } from '../i18n/index.js';
  import { activeCompanyId, companiesLoading } from '../stores/company.js';
  import { companyInvoicesApi } from '../stores/api.js';
  import ErrorBanner from '../components/ErrorBanner.svelte';
  import EmptyState from '../components/EmptyState.svelte';
  import { formatDate, formatEuro } from '../lib/labels.js';

  let drafts = [];
  let loading = true;
  let error = null;

  // activeCompanyId è valorizzato in modo sincrono da localStorage, quindi può
  // indicare un'azienda a cui l'utente non appartiene più finché
  // loadCompanies() non si è risolto. Per questo la lista resta in stato di
  // caricamento finché anche $companiesLoading non torna false, invece di
  // mostrare per un attimo un elenco vuoto che in realtà non è ancora stato letto.
  $: companyId = $activeCompanyId;
  $: if (companyId) load();

  async function load() {
    loading = true;
    error = null;
    try {
      drafts = await companyInvoicesApi.listDrafts(companyId);
    } catch (err) {
      error = err;
    } finally {
      loading = false;
    }
  }

  async function create() {
    try {
      const draft = await companyInvoicesApi.createDraft(companyId, {});
      push(`/drafts/${draft.id}`);
    } catch (err) {
      error = err;
    }
  }

  async function remove(draft) {
    if (!confirm($t('drafts.deleteConfirm'))) return;
    try {
      await companyInvoicesApi.deleteDraft(companyId, draft.id);
      await load();
    } catch (err) {
      error = err;
    }
  }

  /** Una bozza di autofattura: il documento lo scrive l'azienda a se stessa. */
  const isAutofattura = (draft) => Boolean(draft?.metadata?.autofattura);

  /**
   * Nome della controparte, con lo stesso ordine di fallback usato in
   * Invoices.svelte: denominazione, poi nome + cognome per una persona fisica.
   *
   * Su una bozza ordinaria la controparte è il cessionario, perché il documento
   * lo emette l'azienda. Su una bozza di autofattura le parti sono invertite —
   * il cessionario è l'azienda stessa — quindi la controparte è il cedente,
   * cioè il fornitore estero: leggendo il cessionario l'elenco mostrerebbe il
   * nome dell'azienda su ogni riga di autofattura.
   */
  function counterpart(draft) {
    const party = isAutofattura(draft) ? draft?.metadata?.cedente : draft?.metadata?.cessionario;
    if (party) {
      if (party.denominazione) return party.denominazione;
      const full = [party.nome, party.cognome].filter(Boolean).join(' ').trim();
      if (full) return full;
    }
    return '-';
  }

  /** Codice TipoDocumento della bozza, per la pastiglia accanto al nome. */
  function tipoCode(draft) {
    return draft?.tipoDocumento || draft?.metadata?.datiGenerali?.tipoDocumento || null;
  }
</script>

<Layout>
  <div class="view-head">
    <div class="view-head-copy">
      <h1 class="section-title">{$t('drafts.title')}</h1>
      <p class="view-subtitle">{$t('drafts.intro')}</p>
      <p class="view-subtitle mt-2">{$t('drafts.numberNote')}</p>
    </div>
    <div class="view-actions">
      <button type="button" class="btn btn-primary gap-2" on:click={create}>
        <Plus size={16} aria-hidden="true" /> {$t('drafts.new')}
      </button>
    </div>
  </div>

  <InvoiceTabs active="drafts" count={loading || $companiesLoading ? null : drafts.length} />

  <ErrorBanner {error} />

  <div class="mt-4">
    {#if loading || $companiesLoading}
      <p class="p-8 text-center text-sm text-muted" role="status">{$t('drafts.loading')}</p>
    {:else if drafts.length === 0}
      <EmptyState icon={FileText} title={$t('drafts.empty')} body={$t('drafts.emptyHint')} />
    {:else}
      <div class="overflow-x-auto">
        <table class="table-modern table-stack table-cards cards-drafts w-full" role="table">
          <thead class="table-header" role="rowgroup">
            <tr role="row">
              <th class="table-cell table-header-cell text-left" role="columnheader">{$t('drafts.counterpart')}</th>
              <th class="table-cell table-header-cell text-right" role="columnheader">{$t('drafts.total')}</th>
              <th class="table-cell table-header-cell text-left" role="columnheader">{$t('drafts.lastChange')}</th>
              <th class="table-cell" role="columnheader"><span class="sr-only">{$t('drafts.actions')}</span></th>
            </tr>
          </thead>
          <tbody role="rowgroup">
            {#each drafts as draft (draft.id)}
              <tr class="table-row" role="row">
                <td class="table-cell cell-party" role="cell" data-label={$t('drafts.counterpart')}>
                  <span class="inline-flex flex-wrap items-center gap-2 cell-strong">
                    {counterpart(draft)}
                    {#if isAutofattura(draft)}
                      <span class="badge badge-blue" title={$t('drafts.autofatturaBadgeHint')}>
                        {$t('drafts.autofatturaBadge')} {tipoCode(draft)}
                      </span>
                    {/if}
                  </span>
                </td>
                <td class="table-cell cell-amount mono text-right" role="cell" data-label={$t('drafts.total')}>{formatEuro(draft.totalAmount)}</td>
                <td class="table-cell cell-date cell-meta text-xs mono" role="cell" data-label={$t('drafts.lastChange')}>{formatDate(draft.updatedAt)}</td>
                <td class="table-cell cell-actions text-right" role="cell" data-label="">
                  <div class="flex items-center gap-1 justify-end">
                    <!-- use:link keeps this a hash navigation: a plain href would
                         trigger a full document reload and throw away the rest
                         of the list state (InvoiceCreate.svelte carries the same
                         warning). -->
                    <a href={`/drafts/${draft.id}`} use:link class="icon-button"
                      title={$t('drafts.edit')} aria-label={$t('drafts.editOne', { controparte: counterpart(draft) })}>
                      <Pencil size={16} aria-hidden="true" />
                    </a>
                    <button type="button" on:click={() => remove(draft)} class="icon-button hover:text-danger"
                      title={$t('drafts.delete')} aria-label={$t('drafts.deleteOne', { controparte: counterpart(draft) })}>
                      <Trash2 size={16} aria-hidden="true" />
                    </button>
                  </div>
                </td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
    {/if}
  </div>
</Layout>
